// Ce que l'interface de la feuille (lot V4) demande au moteur v2, autour d'une feuille ouverte par la façade
// (`openPacksSheet`) : le clavier contextuel, les outils (Calculer, × k, paires qui s'annulent, enlever les
// parenthèses, Remplacer), les jetons d'une ligne, les étapes sautées. Calque des appels de `ui/app.js` du prototype.
//
// Toute la pédagogie reste dans le moteur : ces fonctions ne font que relayer, avec le moteur VU PAR LA FEUILLE
// (`sheetEngine` : fonctions abstraites de l'exercice). Pur, aucun DOM.
import { expandModelPreds } from '../kernel/model-preds.js';
import { parse, toText } from '../kernel/syntax.js';
import { evaluate } from '../kernel/equiv.js';
import { foldAll, keyOf, light, roles } from '../kernel/rewrite.js';
import { groupJuxtaposed } from '../kernel/quantities.js';
import {
  palette,
  toolsFor,
  toolChoices,
  neutralPair,
  substituteChoices,
  skippedSteps,
  bitsOf,
  sheetEngine,
  progress,
} from '../kernel/judge.js';
import { packsEngine } from '../app.js';
import { astToTokens, toEngineText } from './editor-model.js';
import {
  kprogCodeAtoms,
  kprogCodePaletteParts,
  kprogLineAtoms,
  kprogPaletteParts,
} from '../kernel/python/kernel-python.js';

const eng = (sheet) => sheetEngine(packsEngine(), sheet);

/** Le clavier de l'étape : morceaux, lettres, nombres, opérations, fraction, parenthèses. */
export function sheetPalette(sheet, source = null) {
  if (sheet?.ctx?.geo) return geometryPalette(sheet);
  if (sheet?.ctx?.progs) return pythonPalette(sheet);
  const pal = palette(packsEngine(), sheet, { source });
  return withSystem(
    sheet,
    withVocabulary(
      sheet,
      withQuantities(sheet, withDigits(sheet, withUnits(sheet, withFactorials(sheet, pal)))),
    ),
  );
}

// Système d'équations (31/10/2026) : TOUTES les équations de l'énoncé au clavier (le départ n'en montre qu'une), avec
// leurs nombres. Sans elles, la 2e équation et le « 1 » de « x − y = 1 » ne s'écrivaient pas. C'est l'énoncé : rien
// n'est soufflé.
function withSystem(sheet, pal) {
  const eqs = sheet?.ctx?.kernel?.system?.equations;
  if (!Array.isArray(eqs) || !eqs.length) return pal;
  let bits = { numbers: [], chunks: [] };
  try {
    bits = bitsOf(eng(sheet), eqs.map(String));
  } catch {
    return pal;
  }
  const nv = (x) => parseFloat(String(x).replace(',', '.').replace('−', '-'));
  const numbers = [
    ...new Set([...(pal.numbers || []).map(String), ...bits.numbers.map(String)]),
  ].sort((a, b) => nv(a) - nv(b));
  const chunks = [...(pal.chunks || [])];
  for (const e of eqs.map(String)) if (!chunks.includes(e)) chunks.push(e);
  return { ...pal, numbers, chunks };
}

// Le vocabulaire de la feuille au clavier : les noms (x, C, e, i, f′(x), Q₁…), les fonctions (ln, exp, sin, arg) et les
// signes (≈, !, °, ;, →…) qu'écrivent l'énoncé et le corrigé. Ce sont des mots et des signes, jamais des valeurs :
// les nombres restent aux chiffres. Sans eux, une ligne du corrigé ne pouvait pas s'écrire (banc d'essai du 30/10).
function withVocabulary(sheet, pal) {
  const ctx = sheet?.ctx;
  const k = ctx?.kernel ?? ctx?.model?.kernel;
  if (!k) return pal;
  const hidden = new Set(ctx?.model?.units && Array.isArray(ctx.model.goal) ? ctx.model.goal : []);
  const texts = [k.start, ...(ctx.model?.givens ?? []), ...(k.path ?? []), k.answer].filter(
    (t) => typeof t === 'string' && !/^[⊟⊞⑂⊡]/u.test(t),
  );
  const ids = [...(pal.ids || [])];
  const ops = [...(pal.ops || [])];
  let parens = pal.parens;
  const walk = (toks) => {
    for (const t of toks) {
      if (t.k === 'frac') {
        walk(t.num);
        walk(t.den);
      } else if (t.k === 'lp' || t.k === 'rp') parens = true;
      else if (t.k === 'id' && !hidden.has(t.v) && !ids.includes(t.v)) ids.push(t.v);
      else if (t.k === 'op' && !ops.includes(t.v)) ops.push(t.v);
    }
  };
  for (const t of texts) {
    let toks = null;
    try {
      toks = sheetTokens(sheet, t);
    } catch {
      toks = null;
    }
    if (toks) walk(toks);
  }
  return { ...pal, ids, ops, parens };
}

// Feuille « grandeurs et unités » (lot M5) : la grandeur cherchée (« R ») n'est qu'un nom interne — elle n'est
// jamais écrite par l'élève, pas de touche ; l'unité demandée par la question (« en m² », « en h min ») est au
// clavier : c'est l'énoncé qui la donne, rien n'est soufflé.
function withQuantities(sheet, pal) {
  const ctx = sheet?.ctx;
  const spec = ctx?.model?.units;
  if (!ctx?.quantities || !spec) return pal;
  const hidden = new Set(Array.isArray(ctx.model.goal) ? ctx.model.goal : []);
  const ids = (pal.ids || []).filter((x) => !hidden.has(x));
  const known = ctx.quantities.units;
  const ops = [...(pal.ops || [])];
  for (const u of [...String(spec.unit ?? '').split(/\s+/), spec.valueUnit]) {
    if (!u) continue;
    // « m² », « cm³ » : l'unité de base et l'exposant
    const m = /^(.+?)([²³])$/u.exec(u);
    const base = known[u] ? u : m && known[m[1]] ? m[1] : null;
    if (base && !ids.includes(base)) ids.push(base);
    if (m && known[m[1]] && !ops.includes(m[2])) ops.splice(Math.max(0, ops.indexOf('=')), 0, m[2]);
  }
  return { ...pal, ids, ops };
}

// Feuille « diviseurs d'un entier » (30/10) : l'élève essaie 1, 2, 3… — les chiffres au clavier dès l'ouverture
// (aucun diviseur soufflé : ce sont les dix chiffres), et la touche ÷ (« 12 ÷ 3 = 4 » montre une paire).
function withDivisors(claims) {
  return Array.isArray(claims) && claims.some((c) => c?.ok && c.divisors);
}
function withDigits(sheet, pal) {
  if (!withDivisors(sheet?.ctx?.claims)) return pal;
  const ops = pal.ops.includes('÷') ? pal.ops : [...pal.ops, '÷'];
  return { ...pal, ops, digits: true };
}

// Feuille « formule » de physique (lot P1) : les unités de la formule (`kernel.units`) et les données de l'énoncé
// telles qu'écrites (`kernel.given` : « 250 mA ») — leurs nombres et leurs unités, d'un seul bout chacune. Elles sont
// DANS l'énoncé : rien n'est soufflé.
function withUnits(sheet, pal) {
  const k = sheet?.ctx?.kernel ?? sheet?.ctx?.model?.kernel;
  if (!k?.units || typeof k.units !== 'object') return pal;
  const units = new Set(Object.values(k.units).filter((u) => typeof u === 'string'));
  const numbers = [...(pal.numbers || [])].map(String);
  for (const g of Array.isArray(k.given) ? k.given : []) {
    const m = String(g).match(/^\s*([\d\s,]+?)\s*(\S+)\s*$/u);
    if (!m) continue;
    const n = m[1].replace(/\s/g, '');
    if (!numbers.includes(n)) numbers.push(n);
    units.add(m[2]);
  }
  const ids = [...(pal.ids || [])];
  for (const u of units) if (!ids.includes(u)) ids.push(u);
  return { ...pal, ids, numbers };
}

// Une feuille où l'énoncé écrit des factorielles : la touche « ! » et les factorielles de l'énoncé (« 8! », « 6! »,
// « (n + 1)! ») comme bouts. Elles sont DANS l'énoncé : rien n'est soufflé.
function factorialsOf(ast, out = []) {
  if (!ast || typeof ast !== 'object') return out;
  if (ast.t === 'Fact') out.push(ast);
  for (const c of ast.k || []) factorialsOf(c, out);
  return out;
}
function withFactorials(sheet, pal) {
  const table = eng(sheet).table;
  const facts = factorialsOf(sheet?.lines?.[0]?.ast);
  if (!facts.length) return pal;
  const texts = [...new Set(facts.map((f) => toText(f, table)))];
  const chunks = [...(pal.chunks || [])];
  for (const t of texts) if (!chunks.includes(t) && chunks.length < 10) chunks.push(t);
  const ops = pal.ops.includes('!') ? pal.ops : [...pal.ops, '!'];
  return { ...pal, ops, chunks, parens: pal.parens || texts.some((t) => t.startsWith('(')) };
}

// ── Feuille « géométrie déductive » (plugin `geometry`) ──────────────────────
// Une ligne est une propriété (« (IJ) // (BC) », « I milieu de [AB] », « ABC rectangle en A ») ou une relation
// entre longueurs (« BC² = AB² + AC² »). Le moteur la lit comme du texte (`geometry.js`) : l'éditeur la porte en
// jetons — points, droites, segments, figures et longueurs en `id`, relations en `op` — que `toEngineText` relie
// par des espaces, exactement l'écriture que lit le moteur.

/** Les relations écrites entre deux objets, des plus longues aux plus courtes (« rectangle en » avant « rectangle »). */
export const GEOMETRY_RELATIONS = [
  'n’est pas rectangle',
  'cercle de diamètre',
  'médiatrice de',
  'symétrique de',
  'par rapport à',
  'parallélogramme',
  'rectangle en',
  'isocèle en',
  'milieu de',
  'rectangle',
  'losange',
  'carré',
  '//',
  '∦',
  '⊥',
  '∈',
];
const GEO_PT = "[A-Z][′'’]?[₀-₉]*";
const GEO_OBJECT = new RegExp(
  `^(?:\\((?:${GEO_PT}){2}\\)|\\[(?:${GEO_PT}){2}\\]|(?:${GEO_PT})+)`,
  'u',
);
const GEO_NUMBER = /^\d+(?:[,.]\d+)?/u;
const GEO_OPS = ['≠', '≤', '≥', '=', '<', '>', '+', '−', '-', '×', '÷', '²', '³'];

/** Jetons d'une ligne de géométrie (null si un morceau n'est pas lisible). PUR. */
export function geometryTokens(text) {
  let s = String(text).replace(/'/g, '’').trim();
  const out = [];
  while (s.length) {
    s = s.trimStart();
    if (!s.length) break;
    const rel = GEOMETRY_RELATIONS.find(
      (r) => s.startsWith(r) && (s.length === r.length || /[\s([]/u.test(s[r.length])),
    );
    if (rel) {
      out.push({ k: 'op', v: rel });
      s = s.slice(rel.length);
      continue;
    }
    let m = GEO_OBJECT.exec(s);
    if (m) {
      out.push({ k: 'id', v: m[0] });
      s = s.slice(m[0].length);
      continue;
    }
    if ((m = GEO_NUMBER.exec(s))) {
      out.push({ k: 'num', v: m[0].replace('.', ',') });
      s = s.slice(m[0].length);
      continue;
    }
    const op = GEO_OPS.find((o) => s.startsWith(o));
    if (op) {
      out.push({ k: 'op', v: op === '-' ? '−' : op });
      s = s.slice(op.length);
      continue;
    }
    if (s[0] === '(' || s[0] === ')') {
      out.push({ k: s[0] === '(' ? 'lp' : 'rp' });
      s = s.slice(1);
      continue;
    }
    return null;
  }
  return out;
}

/**
 * Le clavier d'une démonstration : les points, droites, segments, figures et longueurs de l'énoncé, puis ceux des
 * autres lignes de la feuille (corrigé, pièges, propositions — des OBJETS seulement, jamais une phrase : c'est le
 * clavier de l'ancienne feuille, déjà publié) ; les relations et opérations que la feuille utilise ; les chiffres
 * quand il y a des longueurs à calculer (aucun résultat n'est soufflé).
 */
function geometryPalette(sheet) {
  const ctx = sheet.ctx;
  const k = ctx.kernel ?? ctx.model?.kernel ?? {};
  const model = ctx.model ?? {};
  const statement = [k.start, ...(model.givens ?? [])].filter(Boolean).map(String);
  const others = [
    k.answer,
    ...(k.path ?? []),
    ...(model.traps ?? []).map((t) => t?.line),
    ...(model.choices ?? []).map((c) => c?.line),
  ]
    .filter(Boolean)
    .map(String);
  const ids = [];
  const numbers = [];
  const used = new Set();
  const lengths = new Set();
  const take = (texts, withNumbers) => {
    const found = [];
    for (const t of texts) {
      for (const tok of geometryTokens(t) ?? []) {
        if (tok.k === 'op') used.add(tok.v);
        else if (tok.k === 'lp' || tok.k === 'rp') used.add('(');
        else if (tok.k === 'num') {
          if (withNumbers && !numbers.includes(tok.v)) numbers.push(tok.v);
        } else {
          // « AB » et « BA » : une seule longueur
          const key = /^[A-Z]{2}$/u.test(tok.v) ? [...tok.v].sort().join('') : tok.v;
          if (lengths.has(key) || ids.includes(tok.v)) continue;
          lengths.add(key);
          found.push(tok.v);
        }
      }
    }
    return found;
  };
  const rank = (v) => (/^[([]/u.test(v) ? 2 : v.length === 1 ? 0 : 1);
  ids.push(...take(statement, true));
  ids.push(...take(others, false).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)));
  // des longueurs à calculer (énoncé ou corrigé) : les chiffres ; une propriété seule n'en a pas besoin
  const metric = [...statement, ...(k.path ?? []).map(String)].some((t) => /[=≠]/u.test(t));
  const rels = GEOMETRY_RELATIONS.filter((r) => used.has(r)).reverse();
  const arith = ['+', '−', '×', '÷', '²'].filter((o) => used.has(o));
  const cmp = ['≠', '='].filter((o) => used.has(o));
  return {
    ids,
    numbers,
    ops: [...rels, ...arith, ...cmp],
    frac: false,
    parens: used.has('('),
    chunks: [],
    ...(metric ? { digits: true, comma: true } : {}),
  };
}

// ── Feuille « tracer / compléter un programme Python » (plugin `python`) ─────────
// Le juge est celui de l'ancien moteur, qui lit le TEXTE de la ligne par ses propres bouts (`kprogLineAtoms` :
// « i = 2 ; s = 3 », « pgcd(84, 36) = 12 », « L = [1, 3] » ; `kprogCodeAtoms` pour une ligne de code). L'éditeur
// porte ces bouts en jetons : un appel « pgcd(84, 36) » est UN jeton ; les espaces ajoutés par `toEngineText` ne
// changent pas la lecture.

const PY_NUM = /^−?\d+(,\d+)?$/u;
const PY_OPS = new Set([
  '=',
  '≈',
  ';',
  '[',
  ']',
  ',',
  '<',
  '>',
  '≤',
  '≥',
  '≠',
  '+',
  '−',
  '×',
  '÷',
  '%',
]);
const isCode = (sheet) => sheet?.ctx?.progs?.mode === 'complete';

/** Jetons d'une ligne de trace ou de code (null si elle n'est pas lisible). */
export function pythonTokens(sheet, text) {
  let atoms;
  try {
    atoms = isCode(sheet) ? kprogCodeAtoms(String(text)) : kprogLineAtoms(String(text));
  } catch {
    return null;
  }
  return atoms.map((a) => {
    if (a === '(') return { k: 'lp' };
    if (a === ')') return { k: 'rp' };
    if (isCode(sheet)) {
      if (/^\d/u.test(a)) return { k: 'num', v: a };
      if (/^[A-Za-z_À-ɏ"']/u.test(a)) return { k: 'id', v: a };
      return { k: 'op', v: a };
    }
    if (PY_NUM.test(a)) return { k: 'num', v: a };
    if (PY_OPS.has(a)) return { k: 'op', v: a };
    return { k: 'id', v: a };
  });
}

/**
 * Le clavier d'une trace : les variables suivies et les appels du programme (« pgcd(84, 36) »), True / False /
 * None si la feuille en parle, les dix chiffres (aucune valeur de la trace n'est soufflée), puis « = », « ; », les
 * comparaisons et les signes d'une liste quand la feuille s'en sert. Une ligne de code à compléter : les mots de
 * Python, les noms et les nombres du programme, les signes de code (clavier de l'ancienne feuille, déjà publié).
 */
function pythonPalette(sheet) {
  const model = sheet.ctx.model;
  const k = sheet.ctx.kernel ?? {};
  if (isCode(sheet)) {
    const parts = kprogCodePaletteParts(model);
    return {
      ids: [...parts.words, ...parts.names],
      numbers: parts.numbers,
      ops: parts.ops.filter((o) => o !== '(' && o !== ')'),
      frac: false,
      parens: parts.ops.includes('(') || parts.ops.includes(')'),
      chunks: [],
    };
  }
  const parts = kprogPaletteParts(model);
  const texts = [
    k.answer,
    ...(k.path ?? []),
    ...(model.traps ?? []).map((t) => t?.line),
    ...(model.choices ?? []).map((c) => c?.line),
  ].filter(Boolean);
  const atoms = texts.flatMap((t) => {
    try {
      return kprogLineAtoms(String(t));
    } catch {
      return [];
    }
  });
  const used = new Set(atoms);
  const ids = [...parts.names, ...parts.calls];
  // les autres noms du programme dont la feuille parle (paramètre d'une fonction : « x < 0 », « n % 2 = 1 »)
  const code = String(k.program?.code ?? '');
  for (const a of atoms)
    if (
      /^[A-Za-z_À-ɏ][A-Za-z0-9_À-ɏ]*$/u.test(a) &&
      !ids.includes(a) &&
      !['True', 'False', 'None'].includes(a) &&
      new RegExp(`(^|[^A-Za-z0-9_À-ɏ])${a}([^A-Za-z0-9_À-ɏ]|$)`, 'u').test(code)
    )
      ids.push(a);
  for (const w of ['True', 'False', 'None']) if (used.has(w)) ids.push(w);
  const lists = used.has('[');
  const ops = ['=', ...(parts.names.length > 1 || used.has(';') ? [';'] : [])];
  for (const o of ['≈', '<', '>', '≤', '≥', '≠', '+', '−', '×', '÷', '%'])
    if (used.has(o)) ops.push(o);
  if (atoms.some((a) => a.startsWith('−') && a.length > 1) && !ops.includes('−')) ops.push('−');
  if (lists) ops.push('[', ']', ',');
  return {
    ids,
    numbers: [],
    ops,
    frac: false,
    parens: used.has('('),
    chunks: [],
    digits: true,
    // la virgule décimale (« s = 0,5 ») ; dans une liste, « , » sépare deux éléments (touche d'opération)
    ...(!lists && atoms.some((a) => /\d,\d/u.test(a)) ? { comma: true } : {}),
  };
}

/** Toutes les touches d'opération (« Plus de touches »), dans l'ordre du moteur. */
export function sheetFullKeys(sheet) {
  // démonstration : toutes les relations, les opérations sur les longueurs
  if (sheet?.ctx?.geo)
    return [...GEOMETRY_RELATIONS].reverse().concat(['+', '−', '×', '÷', '²', '≠', '=']);
  if (sheet?.ctx?.progs) return sheetPalette(sheet).ops;
  return (
    eng(sheet)?.keyboard?.full || [
      '+',
      '−',
      '×',
      '÷',
      '²',
      '³',
      '^',
      '=',
      '≤',
      '≥',
      '<',
      '>',
      'ou',
      '√',
    ]
  );
}

/** Une ligne juste sans HORA se montre sans commentaire (politique d'affichage du pack core). */
export function sheetNeutralStatus(sheet, status) {
  return eng(sheet)?.display?.[status] === 'neutral';
}

/** Les outils du moteur qui s'appliquent à un genre de nœud (« Div », « Add », « *numeric »…). */
export function sheetToolsFor(sheet, nodeType) {
  return toolsFor(eng(sheet), nodeType);
}

/** Les choix d'un outil pour une ligne (et, pour une fraction, le chemin du quotient visé dans l'AST). */
export function sheetToolChoices(sheet, toolKey, lineText, targetPath = []) {
  return toolChoices(packsEngine(), sheet, toolKey, lineText, targetPath);
}

/** Les résultats proposés pour un calcul ou un monôme sélectionné (le bon parmi des erreurs plausibles). */
export function sheetProposals(sheet, selectionText) {
  const e = eng(sheet);
  for (const tool of [...toolsFor(e, '*numeric'), ...toolsFor(e, '*monomial')]) {
    let v = [];
    try {
      v = toolChoices(packsEngine(), sheet, tool.key, selectionText, [])?.values || [];
    } catch {
      v = [];
    }
    if (v.length) return v.map(String);
  }
  return roundedProposals(sheet, selectionText);
}

// Le nombre de décimales d'une réponse arrondie : `model.rounding.places`, sinon lu sur une ligne « ≈ » du corrigé
// ou de la réponse ; null si la feuille ne demande pas d'arrondi.
function roundingPlaces(sheet) {
  const m = sheet?.ctx?.model;
  if (Number.isInteger(m?.rounding?.places)) return m.rounding.places;
  const k = m?.kernel ?? sheet?.ctx?.kernel;
  for (const t of [k?.answer, ...(k?.path ?? [])]) {
    const r = typeof t === 'string' ? /≈\s*[−-]?\d+(?:,(\d+))?/u.exec(t) : null;
    if (r) return r[1]?.length ?? 0;
  }
  return null;
}

// Arrondi (31/10/2026) : un calcul sans valeur exacte écrivable (√(8 ÷ 3)) sur une feuille qui demande un arrondi —
// la valeur arrondie au nombre de décimales demandé, parmi des erreurs plausibles (troncature, une unité de plus ou
// de moins). Sans elle, « s ≈ 1,63 » ne s'écrivait qu'avec les chiffres de « Plus ».
/** Arrondis proposés pour un nombre déjà calculé (« 1,092727 » → 1,093) ; [] si la feuille n'arrondit pas. */
export function sheetRoundings(sheet, numberText) {
  return roundingPlaces(sheet) == null ? [] : roundedProposals(sheet, numberText, false);
}

function roundedProposals(sheet, text, exact = true) {
  const places = roundingPlaces(sheet);
  const table = eng(sheet).table;
  let x = NaN;
  try {
    const v = evaluate(parse(String(text), table), {}, table);
    x = typeof v === 'number' ? v : v && v.q != null ? Number(v.p) / Number(v.q) : NaN;
  } catch {
    x = NaN;
  }
  if (!Number.isFinite(x)) return [];
  const sortNum = (vals) =>
    [...new Set(vals)].sort(
      (u, w) =>
        parseFloat(u.replace('−', '-').replace(',', '.')) -
        parseFloat(w.replace('−', '-').replace(',', '.')),
    );
  // valeur exacte décimale courte (sin(30°) = 0,5, exp(0) = 1) : le calcul que l'outil numérique ne fait pas
  const d3 = Math.round(x * 1000) / 1000;
  if (exact && Math.abs(d3 - x) < 1e-9) {
    const fd = (y) =>
      (y < 0 ? '−' : '') + String(Math.round(Math.abs(y) * 1000) / 1000).replace('.', ',');
    return sortNum([d3, -d3, 2 * d3, d3 === 0 ? 1 : d3 / 2].map(fd));
  }
  if (places == null) return [];
  const f = 10 ** places;
  const r = Math.round(x * f) / f;
  // déjà exact à cette précision : ce n'est pas un arrondi (les valeurs exactes passent par « Calculer »)
  if (Math.abs(r - x) < 1e-12) return [];
  const fmt = (y) => (y < 0 ? '−' : '') + Math.abs(y).toFixed(places).replace('.', ',');
  return sortNum([r, Math.trunc(x * f) / f, r + 1 / f, r - 1 / f].map(fmt));
}

/** Deux opérandes voisins qui s'annulent (null sinon) : { label, identity }. */
export function sheetNeutralPair(sheet, kind, a, b) {
  const e = eng(sheet);
  if (!(e.tools || []).some((t) => t.kind === 'neutral')) return null;
  try {
    return neutralPair(e, kind, a, b);
  } catch {
    return null;
  }
}

/** L'outil « Remplacer » existe-t-il dans ce moteur ? */
export function sheetHasTool(sheet, kind) {
  return (eng(sheet).tools || []).some((t) => t.kind === kind);
}

/** Valeurs proposées pour remplacer une lettre (données de l'énoncé). */
export function sheetSubstituteChoices(sheet, name) {
  return substituteChoices(packsEngine(), sheet, name);
}

/** Jetons d'un texte du moteur (null s'il n'est pas lisible). */
export function sheetTokens(sheet, text) {
  if (sheet?.ctx?.geo) return geometryTokens(text);
  if (sheet?.ctx?.progs) return pythonTokens(sheet, text);
  // sortes qui lisent leurs lignes avec leur propre lecteur (intégrales, matrices, congruences…) : le texte est gardé
  // tel quel, découpé en jetons sans passer par l'arbre
  if (ownReader(sheet))
    return lexicalTokens(text, { glueSup: sheet?.ctx?.model?.kernel?.plugin === 'matrix' });
  const k = sheet?.ctx?.model?.kernel ?? sheet?.ctx?.kernel;
  // physique (kernel.units) : « 250 mA = 0,25 A » se lit avec ses unités — un nombre suivi d'une unité
  if (k?.units && /\d\s*[A-Za-zµΩ°]/u.test(String(text))) return lexicalTokens(text);
  // développement limité : « o(x²) » est un reste, pas o × x²
  if (/(^|[^\p{L}])o\(/u.test(String(text))) return lexicalTokens(text);
  const toks = generalTokens(sheet, text);
  // l'écriture des jetons doit se relire comme le texte : sinon (« o(x²) », « 2ⁿ⁺¹ »…) le découpage lexical
  if (toks && sameReading(sheet, text, toEngineText(toks))) return toks;
  return lexicalTokens(text);
}

const OWN_READER = new Set([
  'tables',
  'integral',
  'partial',
  'riemann',
  'ode',
  'matrix',
  'congruence',
  'arith',
  'bounds',
  'units',
]);
function ownReader(sheet) {
  const k = sheet?.ctx?.model?.kernel ?? sheet?.ctx?.kernel;
  if (!k) return false;
  if (k.system || k.induction) return true;
  // une feuille de grandeurs s'écrit avec l'arbre (unités collées) : elle a déjà sa lecture
  if (k.plugin === 'units') return false;
  return OWN_READER.has(k.plugin);
}

function sameReading(sheet, a, b) {
  if (a.replace(/\s+/g, '') === b.replace(/\s+/g, '')) return true;
  const table = tokenTable(sheet);
  const units = sheet?.ctx?.quantities?.units;
  const read = (t) => {
    const x = units ? groupJuxtaposed(String(t), units) : String(t);
    return keyOf(light(parse(x, table), table));
  };
  try {
    return read(a) === read(b);
  } catch {
    return false;
  }
}

// Découpage lexical : chaque morceau de l'écriture d'origine devient un jeton, rien n'est réécrit (relu à
// l'identique par le moteur, espaces mis à part). Pas de fraction dessinée : « ÷ » reste un signe.
const BAL = String.raw`(?:[^()]|\([^()]*\))*`; // un niveau de parenthèses à l'intérieur
const LEX_ATOMS = [
  /^∂²?[A-Za-z]\/∂[a-z](?:∂[a-z])?(?:\([^()]*\))?/u, // ∂f/∂x, ∂²f/∂x∂y, ∂f/∂x(1, 2)
  new RegExp(`^∫\\(${BAL}\\)`, 'u'), // ∫(0 ; π ÷ 2)
  new RegExp(`^\\]\\(${BAL}\\)`, 'u'), // ](0 ; ln(3)) d'un crochet
  new RegExp(`^[ΣΠ]\\(${BAL}\\)`, 'u'), // Σ(k = 1 ; n)
  /^\((?:[A-Za-z][²³⁰¹⁴⁵⁶⁷⁸⁹ⁿ]*)\)[₀-₉]+/u, // (M²)₂₂, (M⁶)₁₁
  /^\([A-Za-z][²³⁰¹⁴⁵⁶⁷⁸⁹ⁿ⁻]*\)[²³⁰¹⁴⁵⁶⁷⁸⁹⁻]+/u, // (A²)⁵⁰ d'un calcul de matrices
  /^\[\d+\](?!\s*\()/u, // [7] d'une congruence
  new RegExp(`^(?:PGCD|PPCM|pgcd|ppcm)\\(${BAL}\\)`, 'u'),
  /^[A-Za-z][′'″]{0,2}\([A-Za-z0-9,;.\s−+-]*\)(?![′'])/u, // u(x), y′(0), f(1, 2)…
  new RegExp(`^o\\(${BAL}\\)`, 'u'), // o(x²)
];
const LEX_NAME = /^[A-Za-zα-ωΑ-Ω∞πµ][A-Za-zα-ωΑ-Ω]*(?:[′'″]{1,2})?[₀-₉ₙₖₘᵢⱼ₊₋]*/u;
const LEX_NUM = /^\d+(?:[.,]\d+)?/u;
const LEX_SUP = /^[⁰¹²³⁴⁵⁶⁷⁸⁹ⁿᵏᵐ⁺⁻⁽⁾]+/u;
const SUP_PLAIN = {
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9',
  ⁿ: 'n',
  ᵏ: 'k',
  ᵐ: 'm',
  '⁺': '+',
  '⁻': '−',
  '⁽': '(',
  '⁾': ')',
};
const LEX_WORDS = /^(?:ou|et|parmi|donc)\b/u;
const LEX_OPS = [
  '≡',
  '∼',
  '→',
  '≤',
  '≥',
  '≠',
  '≈',
  '÷',
  '×',
  '+',
  '−',
  '-',
  '=',
  '<',
  '>',
  '^',
  ';',
  ',',
  '[',
  ']',
  '|',
  '%',
  '!',
  '°',
  '√',
  '/',
  '*',
  '⊥',
  '//',
  '∈',
  ':',
];

export function lexicalTokens(text, opts = {}) {
  let s = String(text).trim();
  const out = [];
  // un exposant en chiffres hauts : « ² », « ³ » restent des touches ; les autres (« ⁿ⁺¹ », « ¹⁰⁰ ») s'écrivent
  // « ^(n + 1) », que le moteur lit de même et que le clavier permet d'écrire
  const glue = (sup) => {
    // calcul matriciel : « A⁻¹ », « M⁴ », « (A²)⁵⁰ » — le lecteur des matrices veut l'exposant collé
    const last = out[out.length - 1];
    if (opts.glueSup && last && (last.k === 'id' || last.k === 'num')) return void (last.v += sup);
    if (sup === '²' || sup === '³') return void out.push({ k: 'op', v: sup });
    const plain = [...sup].map((c) => SUP_PLAIN[c] ?? c).join('');
    out.push({ k: 'op', v: '^' }, { k: 'lp' }, ...lexicalTokens(plain), { k: 'rp' });
  };
  while (s.length) {
    if (/^\s/u.test(s)) {
      s = s.replace(/^\s+/u, '');
      continue;
    }
    let m;
    if ((m = LEX_SUP.exec(s))) {
      glue(m[0]);
      s = s.slice(m[0].length);
      continue;
    }
    const atom = LEX_ATOMS.map((r) => r.exec(s)).find(Boolean);
    if (atom) {
      out.push({ k: 'id', v: atom[0] });
      s = s.slice(atom[0].length);
      continue;
    }
    if ((m = LEX_WORDS.exec(s))) {
      out.push({ k: 'op', v: m[0] });
      s = s.slice(m[0].length);
      continue;
    }
    // « +∞ » d'une limite : un seul nom
    if ((m = /^\+∞/u.exec(s))) {
      out.push({ k: 'id', v: '+∞' });
      s = s.slice(2);
      continue;
    }
    if ((m = LEX_NUM.exec(s))) {
      out.push({ k: 'num', v: m[0] });
      s = s.slice(m[0].length);
      continue;
    }
    if ((m = LEX_NAME.exec(s))) {
      // une fonction (ln, exp…) collée à un nom : « lnx » n'existe pas, le nom s'arrête à la fonction
      out.push({ k: 'id', v: m[0] });
      s = s.slice(m[0].length);
      continue;
    }
    if (s[0] === '(' || s[0] === ')') {
      out.push({ k: s[0] === '(' ? 'lp' : 'rp' });
      s = s.slice(1);
      continue;
    }
    const op = LEX_OPS.find((o) => s.startsWith(o));
    if (op) {
      out.push({ k: 'op', v: op === '-' ? '−' : op });
      s = s.slice(op.length);
      continue;
    }
    // un caractère inconnu : gardé tel quel
    out.push({ k: 'id', v: s[0] });
    s = s.slice(1);
  }
  return out;
}

function generalTokens(sheet, text) {
  const table = tokenTable(sheet);
  const units = sheet?.ctx?.quantities?.units;
  try {
    // grandeurs : « 1 h 45 min » se relit comme une somme, comme le moteur la lit
    const t = units ? groupJuxtaposed(String(text), units) : String(text);
    return astToTokens(parse(t, table), table);
  } catch {
    return null;
  }
}

const ODE_POINT = /([A-Za-z])([′'″]*)\(\s*(−?[\d,]+)\s*\)/gu;
const ODE_HOLDERS = ['Ψ', 'Ω', 'Ξ', 'Φ', 'Γ', 'Λ'];
function odeTokens(sheet, text) {
  const found = [];
  const t = String(text).replace(ODE_POINT, (m) => {
    const i = found.indexOf(m);
    if (i >= 0) return ODE_HOLDERS[i];
    if (found.length >= ODE_HOLDERS.length) return m;
    found.push(m);
    return ODE_HOLDERS[found.length - 1];
  });
  if (!found.length) return generalTokens(sheet, text);
  const toks = generalTokens(sheet, t);
  if (!toks) return null;
  const back = (arr) =>
    arr.map((k) => {
      const i = k.k === 'id' ? ODE_HOLDERS.indexOf(k.v) : -1;
      if (i >= 0 && found[i]) return { ...k, v: found[i].replace(/\s/g, '') };
      if (k.k === 'frac') return { ...k, num: back(k.num), den: back(k.den) };
      return k;
    });
  return back(toks);
}

/** La table de l'écriture des jetons ; sur une feuille de grandeurs, elle connaît les unités (écrites collées). */
function tokenTable(sheet) {
  const table = eng(sheet).table;
  const units = sheet?.ctx?.quantities?.units;
  return units ? { ...table, unitNames: new Set(Object.keys(units)) } : table;
}

/** Jetons d'un AST du moteur. */
export function sheetAstTokens(sheet, ast) {
  const table = tokenTable(sheet);
  try {
    return astToTokens(ast, table);
  } catch {
    return [];
  }
}

/** Bouts d'une phrase de l'énoncé (ses formules et ses nombres). */
export function sheetBits(sheet, texts) {
  return bitsOf(eng(sheet), texts);
}

/** Étapes sautées d'une ligne juste mais non justifiée. */
export function sheetSkippedSteps(sheet, lineText) {
  try {
    return skippedSteps(packsEngine(), sheet, lineText);
  } catch {
    return { steps: [], bridges: false };
  }
}

/** Étape du corrigé où en est la feuille : { step, total, done }. */
export function sheetStep(sheet) {
  const p = progress(packsEngine(), sheet);
  return { step: p.step, total: sheet?.ctx?.path?.length ?? 0, done: !!p.done };
}

/** Noms des fonctions spéciales de la syntaxe (coefficient binomial, limite) et de l'infini. */
export function sheetSyntaxNames(sheet) {
  const syntax = eng(sheet).table?.syntax || {};
  const fns = syntax.functions || {};
  return {
    binom: Object.entries(fns).find(([, f]) => f.eval === 'binom')?.[0] ?? null,
    limit: Object.keys(fns).find((n) => fns[n].eval === 'limit') ?? null,
    infinity:
      Object.entries(syntax.constants || {}).find(([, c]) => c.value === '+inf')?.[0] ?? null,
  };
}

/**
 * La fraction touchée dans l'éditeur → son chemin dans l'AST de la ligne telle que le moteur la lit. Les fractions
 * de l'éditeur et les quotients de l'AST se suivent dans le même ordre (préfixe, de gauche à droite).
 */
export function sheetFractionTarget(sheet, tokens, fracPath) {
  const order = [];
  const walkTok = (toks, path) =>
    toks.forEach((t, i) => {
      if (t.k === 'frac' || (t.k === 'op' && t.v === '÷')) order.push(JSON.stringify([...path, i]));
      if (t.k === 'frac') {
        walkTok(t.num, [...path, i, 'num']);
        walkTok(t.den, [...path, i, 'den']);
      }
    });
  walkTok(tokens, []);
  const n = order.indexOf(JSON.stringify(fracPath));
  const table = eng(sheet).table;
  let ast;
  try {
    ast = parse(toEngineText(tokens), table);
  } catch {
    return null;
  }
  let count = -1,
    found = null;
  const walkAst = (node, path) => {
    if (found || !node.k) return;
    if (table.nodes[node.t]?.eval === 'div' && ++count === n) {
      found = path;
      return;
    }
    node.k.forEach((c, i) => walkAst(c, [...path, i]));
  };
  walkAst(ast, []);
  return found;
}

// ── Instrument « factorielle » ───────────────────────────────────────────────
// Une factorielle sélectionnée (« 8! », « (n + 1)! ») : ses écritures par la relation n! = n × (n − 1)!, en sortant
// 1, 2 ou 3 facteurs (« 8 × 7! », « 8 × 7 × 6! »…). Toutes sont justes : l'élève choisit celle qui fait apparaître
// la factorielle dont il a besoin (le juge dira si la ligne avance). Rien si la sélection n'est pas une factorielle.
const constLast = (n) =>
  n?.t === 'Add' && n.k.length > 1 && n.k[0].t === 'Num'
    ? { ...n, k: [...n.k.slice(1), n.k[0]] }
    : n;

/**
 * Les écritures proposées pour l'identité touchée (« 4(x + 2) » → « 4x + 4 × 2 », « 4x + 2 »).
 * Tout vient des PACKS : les règles du geste de l'outil et les pièges de même motif. L'ordre est
 * mélangé et rien ne dit laquelle est juste — c'est le juge qui tranche à la validation.
 * `gesture` dit de QUEL geste elles viennent (« develop », « cross-multiply ») : l'app n'ouvre les
 * touches d'un geste que s'il est sorti de la trousse.
 * [] si aucune identité ne s'applique à cette écriture.
 */
export function sheetRewriteChoices(sheet, selectionText) {
  const e = eng(sheet);
  for (const tool of (e?.tools || []).filter((t) => t.kind === 'rewrite')) {
    let res = null;
    try {
      res = toolChoices(packsEngine(), sheet, tool.key, String(selectionText), []);
    } catch {
      res = null;
    }
    if (res?.values?.length > 1)
      return { label: res.label, values: res.values.map(String), gesture: tool.gesture ?? '' };
  }
  return { label: '', values: [], gesture: '' };
}

/**
 * Les deux groupes de termes d'un produit à développer (« 4(x + 2) », « (x + 1)(x − 2) ») : ce qu'il faut
 * multiplier terme par terme. Chaque terme garde son écriture (`label`, son signe compris) et chaque paire a
 * son produit, écrit SANS les signes (`products[i][j]`) — le signe de liaison reste le geste de l'élève, c'est
 * la règle des signes. null quand l'écriture touchée n'est pas un produit de sommes, ou quand aucun pack
 * n'autorise à développer ici.
 */
export function sheetDistributeGroups(sheet, selectionText) {
  const e = eng(sheet);
  const tool = (e?.tools || []).find((t) => t.kind === 'rewrite');
  if (!tool) return null;
  const table = tokenTable(sheet);
  let ast = null;
  try {
    ast = parse(String(selectionText), table);
  } catch {
    return null;
  }
  const R = roles(table);
  if (ast?.t !== R.mul || ast.k?.length !== 2) return null;
  const termsOf = (n) => (n?.t === R.add ? n.k : [n]);
  const groups = ast.k.map(termsOf);
  // un seul terme de chaque côté : il n'y a rien à distribuer
  if (!groups.some((g) => g.length > 1)) return null;
  // un terme qui cache lui-même une somme (« (x + 1) × (y + 2) ») : ce n'est pas un terme, on ne distribue pas
  const hasSum = (n) => n?.t === R.add || (n?.k || []).some(hasSum);
  if (groups.some((g) => g.some(hasSum))) return null;
  // le terme sans son signe : « −3 » s'écrit « 3 », le signe est choisi à part
  const bare = (n) => (n?.t === R.neg ? n.k[0] : n?.t === 'Num' && n.p < 0 ? { ...n, p: -n.p } : n);
  const txt = (n) => {
    const s = toText(bare(n), table);
    // une division ou un signe de tête se lisent mal collés à « × » : on les encadre
    return /÷/.test(s) || s.trimStart().startsWith('−') ? `(${s})` : s;
  };
  return {
    label: tool.label,
    groups: groups.map((g) => g.map((n) => ({ label: toText(n, table) }))),
    // « × » toujours écrit : « 1 × x » se lit, « 1x » surprend
    products: groups[0].map((a) => groups[1].map((b) => `${txt(a)} × ${txt(b)}`)),
  };
}

export function sheetFactorialChoices(sheet, selectionText, max = 3) {
  const table = eng(sheet).table;
  let ast;
  try {
    ast = parse(String(selectionText), table);
  } catch {
    return [];
  }
  if (ast?.t !== 'Fact') return [];
  const arg = ast.k[0];
  const argText = toText(arg, table);
  const numeric = arg.t === 'Num' && arg.q === 1;
  const minus = (k) => {
    if (numeric) return String(arg.p - k);
    try {
      return toText(constLast(foldAll(parse(`(${argText}) − ${k}`, table), table)), table);
    } catch {
      return null;
    }
  };
  const factor = (t) => (/[+−]/.test(t.replace(/^−/, '')) ? `(${t})` : t);
  const out = [];
  const factors = [factor(argText)];
  for (let d = 1; d <= max; d++) {
    if (numeric && arg.p - d < 0) break;
    const rest = minus(d);
    if (rest == null) break;
    const text = `${factors.join(' × ')} × ${factor(rest)}!`;
    out.push(text);
    if (numeric && arg.p - d <= 1) break;
    const next = minus(d);
    factors.push(factor(next));
  }
  return out;
}

/**
 * Les OUTILS de la feuille, tels que le moteur les accepte d'un bond : les `model.preds`, y compris
 * les preds STRUCTURÉS (Pythagore, second degré, baisse) qui n'ont pas de `line` écrite et que le
 * moteur développe lui-même — avec leurs PIÈGES (les erreurs classiques qu'il refuse). On ne
 * redérive rien ici : on demande au moteur.
 */
export function sheetTools(model) {
  const preds = model && typeof model === 'object' ? model.preds : null;
  if (!Array.isArray(preds)) return { lines: [], traps: [] };
  const { preds: out, traps } = expandModelPreds(preds);
  const str = (v) => (typeof v === 'string' ? v : '');
  return {
    lines: out.map((p) => str(p?.line)).filter((l) => l.length > 0),
    traps: traps
      .map((t) => ({ line: str(t?.line), title: str(t?.title), why: str(t?.why) }))
      .filter((t) => t.line.length > 0),
  };
}

/** Les seules lignes des outils (raccourci de `sheetTools`). */
export function sheetToolLines(model) {
  return sheetTools(model).lines;
}
